import React, { useState, useRef, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import './CustomSelect.css';

/**
 * Composant Select personnalisé qui remplace react-select
 * pour éviter les problèmes de portail et d'erreurs
 */
const CustomSelect = ({
  options = [],
  placeholder = "Sélectionner...",
  isClearable = true,
  formatCreateLabel,
  onCreateOption,
  value,
  onChange,
  onBlur,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [canCreate, setCanCreate] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Fermer le menu si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setCanCreate(false);
        if (onBlur) {
          onBlur();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onBlur]);

  // Filtrer les options selon le terme de recherche
  const filteredOptions = options.filter(option => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const label = option.label?.toLowerCase() || '';
    const valueStr = option.value?.toString().toLowerCase() || '';
    return label.includes(search) || valueStr.includes(search);
  });

  // Vérifier si on peut créer une nouvelle option
  useEffect(() => {
    if (searchTerm && onCreateOption) {
      const exists = options.some(
        opt => opt.value?.toString().toLowerCase() === searchTerm.toLowerCase() ||
               opt.label?.toLowerCase() === searchTerm.toLowerCase()
      );
      setCanCreate(!exists && searchTerm.trim().length > 0);
    } else {
      setCanCreate(false);
    }
  }, [searchTerm, options, onCreateOption]);

  const handleSelect = (option) => {
    if (onChange) {
      onChange(option);
    }
    setIsOpen(false);
    setSearchTerm('');
    setCanCreate(false);
  };

  const handleCreate = () => {
    if (onCreateOption && searchTerm.trim()) {
      const newOption = onCreateOption(searchTerm.trim());
      if (onChange) {
        onChange(newOption);
      }
      setIsOpen(false);
      setSearchTerm('');
      setCanCreate(false);
    } else if (searchTerm.trim()) {
      // Fallback si onCreateOption n'est pas fourni
      const newOption = { value: searchTerm.trim(), label: searchTerm.trim() };
      if (onChange) {
        onChange(newOption);
      }
      setIsOpen(false);
      setSearchTerm('');
      setCanCreate(false);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (onChange) {
      onChange(null);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const displayValue = value ? (value.label || value.value || value) : '';

  return (
    <div className="custom-select-wrapper" ref={wrapperRef}>
      <div
        className={`custom-select-control ${isOpen ? 'is-open' : ''}`}
        onClick={() => {
          if (!isOpen) {
            setIsOpen(true);
            setTimeout(() => {
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }, 0);
          }
        }}
      >
        {!isOpen ? (
          <>
            <span className={`custom-select-value ${!displayValue ? 'custom-select-placeholder' : ''}`}>
              {displayValue || placeholder}
            </span>
            <div className="custom-select-indicators">
              {isClearable && value && (
                <span
                  className="custom-select-clear"
                  onClick={handleClear}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  ×
                </span>
              )}
              <span className="custom-select-arrow">▼</span>
            </div>
          </>
        ) : (
          <input
            ref={inputRef}
            type="text"
            className="custom-select-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={placeholder}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canCreate) {
                e.preventDefault();
                handleCreate();
              } else if (e.key === 'Escape') {
                setIsOpen(false);
                setSearchTerm('');
              } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                // Navigation au clavier pourrait être ajoutée ici
              }
            }}
            autoFocus
          />
        )}
      </div>

      {isOpen && (
        <div className="custom-select-menu">
          {filteredOptions.length > 0 ? (
            <div className="custom-select-menu-list">
              {filteredOptions.map((option, index) => {
                const optionValue = option.value?.toString() || '';
                const optionLabel = option.label || optionValue;
                const isSelected = value && (
                  (typeof value === 'object' && value.value === option.value) ||
                  value === option.value ||
                  value === option
                );

                return (
                  <div
                    key={index}
                    className={`custom-select-option ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => handleSelect(option)}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {optionLabel}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="custom-select-no-options">
              Aucune option trouvée
            </div>
          )}

          {canCreate && onCreateOption && (
            <div
              className="custom-select-create-option"
              onClick={handleCreate}
              onMouseDown={(e) => e.preventDefault()}
            >
              {formatCreateLabel ? formatCreateLabel(searchTerm) : `Créer "${searchTerm}"`}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Wrapper pour react-hook-form
 */
const CustomSelectController = ({ control, name, rules, ...props }) => {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, onBlur, value, name: fieldName } }) => (
        <CustomSelect
          name={fieldName}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          {...props}
        />
      )}
    />
  );
};

export default CustomSelectController;

